import { type ComponentProps, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { type WorkflowData } from '../../../workflow';
import { type TaskComponentType } from '../../../workflow/schemaTypes';
import { useReasons } from '../../../hooks/useReasons';
import { useWorkflow } from '../../../workflow/hooks';
import { useShipmentScope } from '../../../hooks/useShipmentScope';
import { hasAllResolutionsShipmentScope } from '../../../utils/reasonUtils';
import { type Photo } from './ReasonsTaskSchema';
import { ReasonsTaskView } from './ReasonsTaskView';

export const ReasonsTask: TaskComponentType = ({ itemId }) => {
  const { control, register, getValues, resetField, setValue, watch } =
    useFormContext<WorkflowData>();

  // Watch the reasonId so component re-renders when it changes
  // This ensures useReasons() re-runs and nested reasons appear
  watch(`items.${itemId}.reason.reasonId`);

  const reasonsByItem = useReasons();
  const reasonsForItem = itemId ? reasonsByItem[itemId] : null;
  const { order } = useWorkflow();
  const { handleReasonChange } = useShipmentScope(itemId);
  const {
    isNestedReasonRequired,
    reasons,
    nestedReasons,
    selectedRootReason,
    commentConfig,
    photoConfig,
    attestationConfig,
  } = reasonsForItem ?? {
    isCommentEnabled: false,
    isCommentRequired: false,
    isNestedReasonRequired: false,
    nestedReasons: [],
    reasons: [],
    selectedNestedReason: undefined,
    selectedRootReason: undefined,
    commentConfig: {
      enabled: false,
      required: false,
    },
    photoConfig: {
      enabled: false,
      required: false,
    },
    attestationConfig: {
      enabled: false,
      required: false,
    },
  };

  const reasonId = selectedRootReason?.code;
  const shouldShowPreview =
    selectedRootReason &&
    order?.items &&
    itemId &&
    hasAllResolutionsShipmentScope(selectedRootReason);
  const photos = watch(`items.${itemId}.reason.photos`);
  const attestationChecked = watch(
    `items.${itemId}.reason.attestation.checked`,
  );

  useEffect(() => {
    if (!reasonId) return;
    setValue(
      `items.${itemId}.reason.isNestedReasonRequired`,
      isNestedReasonRequired.toString(),
      { shouldValidate: true, shouldDirty: true },
    );
  }, [isNestedReasonRequired, itemId, reasonId, setValue]);

  useEffect(() => {
    if (!reasonId) return;
    setValue(
      `items.${itemId}.reason.comments.metadataId`,
      commentConfig?.metadataId,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
    setValue(
      `items.${itemId}.reason.isCommentRequired`,
      commentConfig?.required.toString(),
      { shouldValidate: true, shouldDirty: true },
    );
    setValue(
      `items.${itemId}.reason.isPhotoRequired`,
      photoConfig?.required.toString(),
      { shouldValidate: true, shouldDirty: true },
    );
    setValue(
      `items.${itemId}.reason.attestation.metadataId`,
      attestationConfig?.metadataId,
      { shouldValidate: true, shouldDirty: true },
    );
    setValue(
      `items.${itemId}.reason.isAttestationRequired`,
      attestationConfig?.required ?? false,
      { shouldValidate: true, shouldDirty: true },
    );
  }, [
    commentConfig?.metadataId,
    commentConfig?.required,
    photoConfig?.required,
    attestationConfig?.metadataId,
    attestationConfig?.required,
    itemId,
    reasonId,
    setValue,
  ]);

  const handleChange: ComponentProps<'select'>['onChange'] = (e) => {
    const { name } = e.target;

    if (
      name === `items.${itemId}.reason.reasonId` &&
      getValues(`items.${itemId}.reason.nestedReasonId`)
    ) {
      resetField(`items.${itemId}.reason.nestedReasonId`, {
        defaultValue: undefined,
      });
    }

    resetField(`items.${itemId}.resolution.resolutionId`, {
      defaultValue: undefined,
    });
    resetField('returnMethod.type', { defaultValue: undefined });
    resetField('returnMethod.location', { defaultValue: undefined });
    resetField(`items.${itemId}.reason.attestation.checked`, {
      defaultValue: undefined,
    });

    // Trigger shipment scope logic when reason changes
    handleReasonChange();
  };

  const handlePhotoUpload = (photos: Photo[]) => {
    setValue(`items.${itemId}.reason.photos`, photos, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handlePhotoRemove = (selectedPhoto: Photo) => {
    const newPhotos = photos?.filter(
      (photo: { id: string }) => photo.id !== selectedPhoto.id,
    );
    setValue(`items.${itemId}.reason.photos`, newPhotos, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleResetPhotos = () => setValue(`items.${itemId}.reason.photos`, []);

  const handleAttestationChange = (checked: boolean) => {
    setValue(`items.${itemId}.reason.attestation.checked`, checked, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  return (
    <ReasonsTaskView
      itemId={itemId}
      reasons={reasons}
      nestedReasons={nestedReasons}
      isNestedReasonRequired={isNestedReasonRequired}
      selectedRootReason={selectedRootReason}
      shouldShowPreview={Boolean(shouldShowPreview)}
      order={order}
      photos={photos}
      attestationChecked={attestationChecked}
      reasonId={reasonId}
      commentConfig={commentConfig}
      photoConfig={photoConfig}
      attestationConfig={attestationConfig}
      onReasonChange={handleChange}
      onPhotoUpload={handlePhotoUpload}
      onPhotoRemove={handlePhotoRemove}
      onPhotoReset={handleResetPhotos}
      onAttestationChange={handleAttestationChange}
      control={control}
      register={register}
    />
  );
};
